import { useRef } from 'react';
import { HOME } from '@/data/home';
import { Reveal, useInView, useMotion, useViewportProgress } from '@components/motion';
import FigmaFrame from './FigmaFrame';

const CIRCLE_GLOW = '/landing/tackled-it/circle-glow.svg';
const CIRCLE_RING = '/landing/tackled-it/circle-ring.svg';

const GRAD_STOPS = 'rgb(255, 135, 54) 30.771%, rgb(255, 102, 0) 58.856%, rgb(196, 78, 0) 89.389%';
const GRAD_ANGLE = 137.458;
const gradientAt = (angle: number) => `linear-gradient(${angle}deg, ${GRAD_STOPS})`;
const GRADIENT = gradientAt(GRAD_ANGLE);

function TackledItDesktop() {
    const sectionRef = useRef<HTMLDivElement>(null);
    const gradientRef = useRef<HTMLDivElement>(null);
    const progress = useViewportProgress(sectionRef);
    const revealed = useInView(sectionRef, { threshold: 0.25 });

    // Sweep the gradient's angle as the section scrolls through the viewport.
    useMotion(gradientRef, progress, (el, p) => {
        el.style.backgroundImage = gradientAt(GRAD_ANGLE + p * 150);
    });

    return (
        <FigmaFrame designWidth={1920} designHeight={1101} className="w-full">
            <div ref={sectionRef} className="relative h-[1101px] w-[1920px]">
                {/* Orange gradient background — its angle animates on scroll */}
                <div
                    ref={gradientRef}
                    className="absolute left-1/2 top-1/2 h-[1101px] w-[1920px] -translate-x-1/2 -translate-y-1/2 backdrop-blur-[11.55px]"
                    style={{ backgroundImage: GRADIENT }}
                />

                {/* Decorative concentric circles, centered — scale up softly behind the
                    text (Reveal), while the ring spins perpetually (`cg-spin`). */}
                <Reveal
                    as="div"
                    once
                    variant="scale"
                    duration={1400}
                    className="absolute left-[730px] top-[336px] size-[457px]"
                >
                    <div className={`size-full will-change-transform ${revealed ? 'cg-spin' : ''}`}>
                        <img src={CIRCLE_GLOW} alt="" className="absolute inset-0 block size-full max-w-none" />
                        <div className="absolute inset-[-0.12%_-0.6%_-0.25%_-0.17%]">
                            <img src={CIRCLE_RING} alt="" className="block size-full max-w-none" />
                        </div>
                    </div>
                </Reveal>

                {/* Heading */}
                <Reveal
                    as="div"
                    once
                    variant="fade-up"
                    delay={200}
                    duration={1000}
                    className="absolute left-[calc(50%-492px)] top-[calc(50%+0.5px)] flex -translate-y-1/2 flex-col justify-center whitespace-nowrap text-[72px] font-semibold leading-[92px] text-white"
                >
                    <p className="leading-[92px]">{HOME.tackled.heading}</p>
                </Reveal>
            </div>
        </FigmaFrame>
    );
}

function TackledItMobile() {
    const sectionRef = useRef<HTMLElement>(null);
    const revealed = useInView(sectionRef, { threshold: 0.25 });
    return (
        <section
            ref={sectionRef}
            className="relative flex min-h-[600px] items-center justify-center overflow-hidden backdrop-blur-[11.55px]"
            style={{ backgroundImage: GRADIENT }}
        >
            {/* Decorative concentric circles, centered behind text */}
            <Reveal
                as="div"
                once
                variant="scale"
                duration={1300}
                className="pointer-events-none absolute left-1/2 top-1/2 size-[252px] -translate-x-1/2 -translate-y-1/2"
            >
                <div className={`size-full will-change-transform ${revealed ? 'cg-spin' : ''}`}>
                    <img src={CIRCLE_GLOW} alt="" className="absolute inset-0 block size-full max-w-none" />
                    <div className="absolute inset-[-0.23%_-1.13%_-0.48%_-0.3%]">
                        <img src={CIRCLE_RING} alt="" className="block size-full max-w-none" />
                    </div>
                </div>
            </Reveal>

            {/* Heading */}
            <Reveal
                as="div"
                once
                variant="fade-up"
                delay={200}
                className="relative flex w-[335px] flex-col justify-center px-6 text-center text-[40px] font-semibold leading-[50px] text-white"
            >
                <p className="leading-[50px]">{HOME.tackled.heading}</p>
            </Reveal>
        </section>
    );
}

export default function TackledIt() {
    return (
        <>
            <div className="hidden lg:block">
                <TackledItDesktop />
            </div>
            <div className="lg:hidden">
                <TackledItMobile />
            </div>
        </>
    );
}
